<template>
  <div class="referral-list">
    <f7-block-title medium class="referral-list__title">
      {{ $t('referral.list.title') }}
    </f7-block-title>

    <!-- Loading skeletons -->
    <f7-list v-if="loading && rows.length === 0" media-list inset class="referral-list__skeleton">
      <f7-list-item v-for="n in 3" :key="n">
        <template #media>
          <f7-skeleton-block
            style="width: 40px; height: 40px; border-radius: 12px"
          ></f7-skeleton-block>
        </template>
        <template #title>
          <f7-skeleton-block style="width: 120px; height: 14px"></f7-skeleton-block>
        </template>
        <template #after>
          <f7-skeleton-block style="width: 70px; height: 14px"></f7-skeleton-block>
        </template>
        <template #footer>
          <f7-skeleton-block style="width: 90px; height: 10px"></f7-skeleton-block>
        </template>
      </f7-list-item>
    </f7-list>

    <!-- Error -->
    <f7-block v-else-if="error" class="text-align-center referral-list__error">
      <f7-icon
        f7="exclamationmark_triangle_fill"
        size="40"
        color="orange"
        class="margin-bottom-half"
      ></f7-icon>
      <p class="text-color-gray">{{ error }}</p>
      <f7-button outline class="margin-top-half" @click="$emit('retry')">
        {{ $t('wallet.retry') }}
      </f7-button>
    </f7-block>

    <!-- Empty -->
    <f7-block v-else-if="rows.length === 0" class="text-align-center referral-list__empty">
      <f7-icon f7="person_2_fill" size="48" color="gray" class="margin-bottom-half"></f7-icon>
      <p class="text-color-gray">{{ $t('referral.list.empty') }}</p>
    </f7-block>

    <!-- Rows -->
    <f7-list
      v-else
      media-list
      strong-ios
      outline-ios
      dividers-ios
      inset
    >
      <f7-list-item v-for="row in rows" :key="row.id">
        <template #title>{{ row.memberName || referredNameOf(row) }}</template>
        <template #media>
          <div class="referral-row__icon" :class="`referral-row__icon--${badgeOf(row).tone}`">
            <f7-icon :f7="badgeOf(row).icon" size="18"></f7-icon>
          </div>
        </template>
        <template #content>
          <div class="referral-row__content">
            <div class="referral-row__meta">
              <div v-if="row.memberName">{{ referredNameOf(row) }}</div>
              <div>{{ joinedAtOf(row) }}</div>
              <div
                class="referral-row__activation"
                :class="{ 'referral-row__activation--on': row.isActivated }"
              >
                {{ $t(row.isActivated ? 'referral.list.activated' : 'referral.list.notActivated') }}
              </div>
            </div>
            <button
              v-if="row.telegramUrl"
              type="button"
              class="referral-row__telegram"
              :aria-label="$t('referral.list.messageOnTelegram')"
              :title="$t('referral.list.messageOnTelegram')"
              @click="openTelegram(row.telegramUrl)"
            >
              <f7-icon f7="paperplane_fill" size="16"></f7-icon>
            </button>
            <div v-if="hasReward(row)" class="referral-row__after">
              <span class="referral-row__amount">{{ formatCurrency(row.rewardCents) }}</span>
              <span
                class="referral-row__badge"
                :class="`referral-row__badge--${badgeOf(row).tone}`"
              >
                {{ $t(badgeOf(row).i18nKey) }}
              </span>
            </div>
          </div>
        </template>
      </f7-list-item>
    </f7-list>
  </div>
</template>

<script setup>
import { useI18n } from 'vue-i18n';
import { useCurrency } from '@/composables/useCurrency';
import { REWARD_STATE } from '@/constants/referral';

defineProps({
  rows: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  error: {
    type: String,
    default: null,
  },
});

defineEmits(['retry']);

const { t, locale } = useI18n();
const { formatCurrency } = useCurrency();

// Frozen state -> badge map, WITH a fallback so an unknown future rewardState
// degrades instead of rendering blank. `under_review` deliberately covers both
// "awaiting an admin decision" AND "declined" — see MemberReferralRewardState's
// docblock. Never add a declined variant, a warning tone, or a hinting tooltip.
const REWARD_STATE_BADGE = Object.freeze({
  [REWARD_STATE.NOT_DUE]: { i18nKey: 'referral.rewardState.not_due', icon: 'clock', tone: 'muted' },
  [REWARD_STATE.UNDER_REVIEW]: {
    i18nKey: 'referral.rewardState.under_review',
    icon: 'hourglass',
    tone: 'muted',
  },
  [REWARD_STATE.EARNED]: {
    i18nKey: 'referral.rewardState.earned',
    icon: 'checkmark_circle_fill',
    tone: 'positive',
  },
  [REWARD_STATE.EXPIRED]: {
    i18nKey: 'referral.rewardState.expired',
    icon: 'xmark_circle',
    tone: 'muted',
  },
  // No owner reward attaches at all — a shop's row, or a member program with
  // no owner reward. The icon is the person, not a money state (see hasReward).
  [REWARD_STATE.NONE]: {
    i18nKey: 'referral.rewardState.none',
    icon: 'person_fill',
    tone: 'muted',
  },
});

const FALLBACK_BADGE = Object.freeze({
  i18nKey: 'referral.rewardState.none',
  icon: 'minus_circle',
  tone: 'muted',
});

function badgeOf(row) {
  return REWARD_STATE_BADGE[row?.rewardState] ?? FALLBACK_BADGE;
}

/**
 * Whether the row carries an owner reward to show. A `none` row — every row of a
 * shop's list, since a shop earns nothing — shows who joined and nothing else,
 * never "0.00 ج.م · لا يوجد" on each line. The row says so itself, so the list
 * needs no page-level flag.
 */
function hasReward(row) {
  return row?.rewardState !== REWARD_STATE.NONE;
}

function referredNameOf(row) {
  return row?.referredName ?? t('referral.list.unknownShop');
}

/**
 * Open a chat with the invitee. `telegramUrl` is always `https://t.me/<username>`
 * (the backend sends none for an invitee without a username). The adapter is
 * imported on click, like utils/supportContact.js, so this component's tests never
 * load the PWA/service-worker chain.
 */
async function openTelegram(url) {
  const { adapter } = await import('@/platform/index.js');
  adapter.openTelegramChat(url);
}

function joinedAtOf(row) {
  const raw = row?.joinedAt;
  if (!raw) return '';
  // ui-western-digits lock: force Latin numerals via `-u-nu-latn` so the Arabic
  // locale never renders Arabic-Indic digits. See PendingGiftsPanel.vue:96-104.
  return new Intl.DateTimeFormat(locale.value === 'ar' ? 'ar-EG-u-nu-latn' : 'en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(new Date(raw));
}
</script>

<style scoped>
.referral-list__title {
  margin-top: 8px;
  margin-bottom: 4px;
}

.referral-list__empty,
.referral-list__error {
  padding-top: 32px;
  padding-bottom: 32px;
}

.referral-list__skeleton {
  pointer-events: none;
}

.referral-row__icon {
  width: 32px;
  height: 32px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: rgba(107, 114, 128, 0.12);
  color: var(--poolit-text-muted, #6b7280);
}

.referral-row__icon--positive {
  background: rgba(122, 182, 251, 0.18);
  color: var(--poolit-secondary-700, #2563eb);
}

.referral-row__content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
}

.referral-row__meta {
  flex: 1;
  min-width: 0;
  font-size: 12px;
  color: var(--poolit-text-muted, #6b7280);
}

.referral-row__activation {
  font-weight: 600;
}

.referral-row__activation--on {
  color: #047857;
}

/* Telegram's own blue, so the target app is recognisable at a glance. */
.referral-row__telegram {
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 50%;
  background: #2aabee;
  color: #fff;
  cursor: pointer;
}

.referral-row__after {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
}

.referral-row__amount {
  font-size: 14px;
  font-weight: 700;
  font-feature-settings:
    'tnum' on,
    'lnum' on;
  color: var(--poolit-text-primary, #1f2937);
}

.referral-row__badge {
  font-size: 11px;
  font-weight: 600;
  padding: 1px 8px;
  border-radius: 999px;
  background: rgba(107, 114, 128, 0.12);
  color: var(--poolit-text-muted, #6b7280);
}

.referral-row__badge--positive {
  background: rgba(16, 185, 129, 0.16);
  color: #047857;
}
</style>
